import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';

export default function ProduitLot3AngesMixte() {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const productId = 'lot-3-anges-mixte';
  const defaultImage = 'https://public.readdy.ai/ai/img_res/6d413a58-1c50-400a-be34-b0910d597568.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  const product = {
    id: 'lot-3-anges-mixte',
    name: 'Lot "Les 3 Anges de Lolotte"',
    price: 10,
    originalPrice: 12,
    description: 'Assortiment mixte des 3 modèles d\'anges : Joyeux Cœur, Rêve Doux et Sérénité. Un trio complet pour une décoration de Noël pleine de tendresse et de poésie.',
    longDescription: 'Découvrez la collection complète des anges de Lolotte dans ce lot unique ! Chaque ange a sa propre personnalité : le Joyeux Cœur rayonnant, le Rêve Doux endormi et la Sérénité apaisante. Créés à partir de pages de livres anciens recyclés, ils apportent une touche d\'âme et de poésie à votre décoration de Noël.',
    dimensions: 'environ 8 cm chacun (hors ficelle)',
    materials: 'pages de livres anciens recyclés, ficelle naturelle',
    colors: 'naturel avec ficelle',
    care: 'À manipuler avec délicatesse, éviter l\'humidité',
    image: 'https://public.readdy.ai/ai/img_res/6d413a58-1c50-400a-be34-b0910d597568.png',
    category: 'Collection de Noël',
    subcategory: 'Anges de Noël',
    inStock: true,
    handmade: true,
    eco: true
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      description: product.description
    }, quantity);

    // Feedback visuel
    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      const originalText = button.innerHTML;
      button.innerHTML = '<i class="ri-check-line mr-2"></i>Ajouté au panier !';
      button.disabled = true;
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <section className="py-4 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <a href="/" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors">Accueil</a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <a href="/boutique" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors">Boutique</a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <a href="/noel" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors">Collection de Noël</a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <span className="text-[#B97A72] font-medium">Lot "Les 3 Anges de Lolotte"</span>
          </nav>
        </div>
      </section>

      {/* Product Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4">
                <img
                  src={productImage}
                  alt="Lot Les 3 Anges de Lolotte"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Bouton pour modifier l'image */}
              <div className="text-center">
                <button
                  onClick={() => setShowImageUploader(!showImageUploader)}
                  className="bg-[#B97A72] text-white px-6 py-3 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-image-edit-line mr-2"></i>
                  {showImageUploader ? 'Fermer' : '📸 Modifier l\'image'}
                </button>
              </div>

              {/* Module de changement d'image */}
              {showImageUploader && (
                <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Modifier l'image du produit
                  </h3>
                  <LocalImageUploader
                    productId={productId}
                    currentImage={productImage}
                    onImageUpdate={handleImageUpdate}
                  />
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <span className="text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.subcategory}
                </span>
                <h1 className="text-4xl font-bold text-[#6B5E5A] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.name}
                </h1>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {product.handmade && (
                  <span className="bg-[#E8CBA8] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Fait main ✋
                  </span>
                )}
                {product.eco && (
                  <span className="bg-[#C49B86] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Éco-responsable 🌱
                  </span>
                )}
                <span className="bg-[#FFF8F5] text-[#C49B86] px-3 py-1 rounded-full text-sm font-medium border border-[#C49B86]">
                  Collection complète 🎄
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center space-x-3">
                <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price}€
                </span>
                {product.originalPrice && (
                  <span className="text-xl text-[#6B5E5A] line-through opacity-60" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.originalPrice}€
                  </span>
                )}
                <span className="bg-[#E8CBA8] text-white px-2 py-1 rounded text-sm font-medium">
                  Économie: {product.originalPrice - product.price}€
                </span>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <p className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.description}
                </p>
                <p className="text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.longDescription}
                </p>
              </div>

              {/* What's included */}
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ce lot contient :
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#B97A72] mr-2"></i>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>1 Ange "Joyeux Cœur" - le rayonnant ✨</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#B97A72] mr-2"></i>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>1 Ange "Rêve Doux" - l'endormi 🌙</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-[#B97A72] mr-2"></i>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>1 Ange "Sérénité" - l'apaisant ☀️</span>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 space-y-3">
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Détails du produit
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="font-medium text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>Dimensions :</span>
                    <span className="text-[#6B5E5A] ml-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.dimensions}</span>
                  </div>
                  <div>
                    <span className="font-medium text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>Matières :</span>
                    <span className="text-[#6B5E5A] ml-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.materials}</span>
                  </div>
                  <div>
                    <span className="font-medium text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>Couleurs :</span>
                    <span className="text-[#6B5E5A] ml-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.colors}</span>
                  </div>
                  <div>
                    <span className="font-medium text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>Entretien :</span>
                    <span className="text-[#6B5E5A] ml-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.care}</span>
                  </div>
                </div>
              </div>

              {/* Quantity and Add to Cart */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>Quantité :</span>
                  <div className="flex items-center border-2 border-[#B97A72] rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="px-4 py-2 font-medium text-[#6B5E5A]">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 bg-[#B97A72] text-white px-8 py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-lg font-medium flex items-center justify-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-shopping-cart-line"></i>
                    </div>
                    Ajouter au panier
                  </button>
                  <a
                    href="/panier"
                    className="bg-transparent text-[#B97A72] px-8 py-4 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium flex items-center justify-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-shopping-bag-line"></i>
                    </div>
                    Voir le panier
                  </a>
                </div>
              </div>

              {/* Stock Status */}
              {product.inStock && (
                <div className="flex items-center text-green-600">
                  <i className="ri-check-line mr-2"></i>
                  <span style={{ fontFamily: 'Poppins, sans-serif' }}>En stock - Expédition sous 2-3 jours</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-[#EBD2CF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#6B5E5A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Fait main</h3>
              <p className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Chaque ange est créé à la main avec patience et minutie dans mon atelier.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#6B5E5A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Éthique</h3>
              <p className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Créations durables à partir de livres recyclés, pour une déco responsable.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#6B5E5A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Cadeau unique</h3>
              <p className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Un présent original qui apporte poésie et tendresse à Noël.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <a
              href="/boutique"
              className="inline-flex items-center text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Retour à la boutique
            </a>
            <a
              href="/noel"
              className="inline-flex items-center text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Voir tous les anges de Noël
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
